/* eslint-disable no-unused-expressions */
import axios from 'axios';
import { prodDb, subDb, userDb as profileDb } from '../../config/pouch';
import { setLoading, setMessage, setOfflineDownloadComplete } from './status';
import { auth } from '../../config/firebase';

export const DOWNLOAD_OFFLINE_DATA_REQUEST = 'DOWNLOAD_OFFLINE_DATA_REQUEST';
export const DOWNLOAD_OFFLINE_DATA_SUCCESS = 'DOWNLOAD_OFFLINE_DATA_SUCCESS';
export const DOWNLOAD_OFFLINE_DATA_FAILURE = 'DOWNLOAD_OFFLINE_DATA_FAILURE';

const API_BASE_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
const CHUNK_SIZE = 100;
const REQUEST_DELAY = 1000;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const getAuthHeaders = async () => {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error('Utilisateur non authentifié. Veuillez vous connecter avant de télécharger les données.');
        }

        // Récupérer le token d'authentification Firebase pour l'API MongoDB
        const idToken = await currentUser.getIdToken();

        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
        };
    } catch (error) {
        throw new Error(`Impossible de vérifier l'authentification: ${error.message}`);
    }
};

const downloadProducts = async (dispatch, onProgress) => {
    try {
        const headers = await getAuthHeaders();
        const allProductsResponse = await axios.get(`${API_BASE_URL}/allProducts`, {
            headers,
            timeout: 10000
        });

        const allProductIds = allProductsResponse.data.products || [];
        const totalProducts = allProductIds.length;
        let downloaded = 0;

        if (totalProducts === 0) {
            return 0;
        }

        for (let offset = 0; offset < totalProducts; offset += CHUNK_SIZE) {
            const chunkIds = allProductIds.slice(offset, offset + CHUNK_SIZE);
            const idsString = chunkIds.map(product => product._id).join(',');

            const response = await axios.post(`${API_BASE_URL}/productsForIds`, {
                ids: idsString
            }, {
                headers,
                timeout: 15000
            });

            const products = response.data.products || [];

            for (const product of products) {
                try {
                    await prodDb.put({
                        _id: product.id || product._id,
                        ...product,
                        downloadId: product.id || product._id,
                        downloadedAt: new Date().toISOString()
                    });
                    downloaded++;
                } catch (error) {
                    if (error.status === 409) {
                        try {
                            const existing = await prodDb.get(product.id || product._id);
                            await prodDb.put({
                                ...existing,
                                ...product,
                                downloadedAt: new Date().toISOString()
                            });
                            downloaded++;
                        } catch (updateError) {
                            // Silent error handling
                        }
                    }
                }
            }

            const progress = Math.round((downloaded / totalProducts) * 100);
            onProgress?.({
                type: 'products',
                current: downloaded,
                total: totalProducts,
                progress
            });

            if (offset + CHUNK_SIZE < totalProducts) {
                await wait(REQUEST_DELAY);
            }
        }

        return downloaded;

    } catch (error) {
        return 0;
    }
};


const downloadSubstances = async (dispatch, onProgress) => {
    try {
        const headers = await getAuthHeaders();

        const allSubstancesResponse = await axios.get(`${API_BASE_URL}/allSubstances`, {
            headers,
            timeout: 10000
        });

        const allSubstanceIds = allSubstancesResponse.data.substances || [];
        const totalSubstances = allSubstanceIds.length;
        let downloaded = 0;

        if (totalSubstances === 0) {
            return 0;
        }

        for (let offset = 0; offset < totalSubstances; offset += CHUNK_SIZE) {
            const chunkIds = allSubstanceIds.slice(offset, offset + CHUNK_SIZE);
            const idsString = chunkIds.map(substance => substance._id).join(',');

            const response = await axios.post(`${API_BASE_URL}/substancesForIds`, {
                ids: idsString
            }, {
                headers,
                timeout: 15000
            });

            const substances = response.data.substances || [];

            for (const substance of substances) {
                try {
                    await subDb.put({
                        _id: substance.id || substance._id,
                        ...substance,
                        downloadId: substance.id || substance._id,
                        downloadedAt: new Date().toISOString()
                    });
                    downloaded++;
                } catch (error) {
                    if (error.status === 409) {
                        try {
                            const existing = await subDb.get(substance.id || substance._id);
                            await subDb.put({
                                ...existing,
                                ...substance,
                                downloadedAt: new Date().toISOString()
                            });
                            downloaded++;
                        } catch (updateError) {
                            // Silent error handling
                        }
                    }
                }
            }

            const progress = Math.round((downloaded / totalSubstances) * 100);
            onProgress?.({
                type: 'substances',
                current: downloaded,
                total: totalSubstances,
                progress
            });

            if (offset + CHUNK_SIZE < totalSubstances) {
                await wait(REQUEST_DELAY);
            }
        }

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const downloadProfiles = async (dispatch, onProgress) => {
    try {
        const headers = await getAuthHeaders();

        const allProfilesResponse = await axios.get(`${API_BASE_URL}/allProfiles`, {
            headers,
            timeout: 10000
        });

        const allProfileIds = allProfilesResponse.data.profiles || [];
        const totalProfiles = allProfileIds.length;
        let downloaded = 0;

        if (totalProfiles === 0) {
            return 0;
        }

        for (let offset = 0; offset < totalProfiles; offset += CHUNK_SIZE) {
            const chunkIds = allProfileIds.slice(offset, offset + CHUNK_SIZE);
            const idsString = chunkIds.map(profile => profile._id).join(',');

            const response = await axios.post(`${API_BASE_URL}/profilesForIds`, {
                ids: idsString
            }, {
                headers,
                timeout: 15000
            });

            const profiles = response.data.profiles || [];

            for (const profile of profiles) {
                try {
                    await profileDb.put({
                        _id: profile.id || profile._id,
                        ...profile,
                        downloadId: profile.id || profile._id,
                        downloadedAt: new Date().toISOString()
                    });
                    downloaded++;
                } catch (error) {
                    if (error.status === 409) {
                        try {
                            const existing = await profileDb.get(profile.id || profile._id);
                            await profileDb.put({
                                ...existing,
                                ...profile,
                                downloadedAt: new Date().toISOString()
                            });
                            downloaded++;
                        } catch (updateError) {}
                    }
                }
            }

            const progress = Math.round((downloaded / totalProfiles) * 100);
            onProgress?.({
                type: 'profiles',
                current: downloaded,
                total: totalProfiles,
                progress
            });

            if (offset + CHUNK_SIZE < totalProfiles) {
                await wait(REQUEST_DELAY);
            }
        }

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const copyLocalJsonData = async (dispatch, onProgress) => {
    try {
        const classesData = await import('../../json/classe.json');
        const dosagesData = await import('../../json/dosage.json');
        const formesData = await import('../../json/forme.json');
        const professionalsData = await import('../../json/professionals.json');
        const profilesData = await import('../../json/profile.json');

        const jsonSources = [
            { name: 'classes', data: classesData.default || classesData },
            { name: 'dosages', data: dosagesData.default || dosagesData },
            { name: 'formes', data: formesData.default || formesData },
            { name: 'professionals', data: professionalsData.default || professionalsData },
            { name: 'profiles', data: profilesData.default || profilesData }
        ];

        let totalElementsCopied = 0;
        const { localDataDb } = await import('../../config/pouch');

        for (let i = 0; i < jsonSources.length; i++) {
            const { name, data } = jsonSources[i];
            const dataArray = Array.isArray(data) ? data : [data];

            try {
                await localDataDb.put({
                    _id: `json_${name}`,
                    type: 'json_collection',
                    name: name,
                    data: dataArray,
                    count: dataArray.length,
                    size: JSON.stringify(dataArray).length,
                    syncedAt: new Date().toISOString(),
                    source: 'local_json'
                });

                for (let j = 0; j < Math.min(dataArray.length, 1000); j++) {
                    try {
                        await localDataDb.put({
                            _id: `${name}_${j}`,
                            type: 'json_item',
                            collection: name,
                            index: j,
                            ...dataArray[j],
                            syncedAt: new Date().toISOString()
                        });
                    } catch (itemError) {
                        if (itemError.name === 'conflict') {
                            try {
                                const existing = await localDataDb.get(`${name}_${j}`);
                                await localDataDb.put({
                                    ...existing,
                                    ...dataArray[j],
                                    syncedAt: new Date().toISOString()
                                });
                            } catch (updateError) {}
                        }
                    }
                }

                totalElementsCopied += dataArray.length;

                const progress = Math.round(((i + 1) / jsonSources.length) * 100);
                onProgress?.({
                    type: 'json_data',
                    current: i + 1,
                    total: jsonSources.length,
                    progress,
                    message: `${name}: ${dataArray.length} éléments`
                });

            } catch (error) {
                if (error.name === 'conflict') {
                    try {
                        const existing = await localDataDb.get(`json_${name}`);
                        await localDataDb.put({
                            ...existing,
                            data: dataArray,
                            count: dataArray.length,
                            size: JSON.stringify(dataArray).length,
                            syncedAt: new Date().toISOString()
                        });
                        totalElementsCopied += dataArray.length;
                    } catch (updateError) {}
                }
            }
        }

        return totalElementsCopied;

    } catch (error) {
        throw new Error(`Échec copie données JSON: ${error.message}`);
    }
};

const downloadProductsFromFirestore = async (dispatch, onProgress) => {
    try {
        const { auth, firestore } = await import('../../config/firebase');
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return 0;
        }

        const { prodDb } = await import('../../config/pouch');
        let downloaded = 0;
        let totalFound = 0;
        let lastDoc = null;
        const batchSize = 1000;

        do {
            let query = firestore.collection('products')
                .orderBy('__name__')
                .limit(batchSize);

            if (lastDoc) {
                query = query.startAfter(lastDoc);
            }

            const snapshot = await query.get();
            const batchCount = snapshot.size;
            totalFound += batchCount;

            if (snapshot.empty) {
                break;
            }

            const batch = [];
            for (const doc of snapshot.docs) {
                const productData = doc.data();
                batch.push({
                    _id: doc.id,
                    ...productData,
                    downloadedAt: new Date().toISOString(),
                    source: 'firestore'
                });
                lastDoc = doc;
            }

            try {
                const bulkResult = await prodDb.bulkDocs(batch, { new_edits: false });

                bulkResult.forEach(result => {
                    if (!result.error) {
                        downloaded++;
                    }
                });

                onProgress?.({
                    type: 'products',
                    current: downloaded,
                    total: totalFound,
                    progress: Math.round((downloaded / Math.max(totalFound, 9000)) * 100),
                    message: `${downloaded}/${totalFound} produits téléchargés`
                });

            } catch (bulkError) {
                for (const product of batch) {
                    try {
                        await prodDb.put(product);
                        downloaded++;
                    } catch (error) {
                        if (error.status === 409) {
                            try {
                                const existing = await prodDb.get(product._id);
                                await prodDb.put({
                                    ...existing,
                                    ...product,
                                });
                                downloaded++;
                            } catch (updateError) {}
                        }
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, 100));

        } while (lastDoc && totalFound < 15000);

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const downloadSubstancesFromFirestore = async (dispatch, onProgress) => {
    try {
        const { auth, firestore } = await import('../../config/firebase');
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return 0;
        }

        const { subDb } = await import('../../config/pouch');
        let downloaded = 0;
        let totalFound = 0;
        let lastDoc = null;
        const batchSize = 1000;

        do {
            let query = firestore.collection('substances')
                .orderBy('__name__')
                .limit(batchSize);

            if (lastDoc) {
                query = query.startAfter(lastDoc);
            }

            const snapshot = await query.get();
            const batchCount = snapshot.size;
            totalFound += batchCount;

            if (snapshot.empty) break;

            const batch = [];
            for (const doc of snapshot.docs) {
                const substanceData = doc.data();
                batch.push({
                    _id: doc.id,
                    ...substanceData,
                    downloadedAt: new Date().toISOString(),
                    source: 'firestore'
                });
                lastDoc = doc;
            }

            try {
                const bulkResult = await subDb.bulkDocs(batch, { new_edits: false });
                bulkResult.forEach(result => {
                    if (!result.error) downloaded++;
                });

                onProgress?.({
                    type: 'substances',
                    current: downloaded,
                    total: totalFound,
                    progress: Math.round((downloaded / Math.max(totalFound, 1000)) * 100),
                    message: `${downloaded}/${totalFound} substances téléchargées`
                });

            } catch (bulkError) {
                for (const substance of batch) {
                    try {
                        await subDb.put(substance);
                        downloaded++;
                    } catch (error) {
                        if (error.status === 409) {
                            try {
                                const existing = await subDb.get(substance._id);
                                await subDb.put({ ...existing, ...substance });
                                downloaded++;
                            } catch (updateError) {}
                        }
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, 50));

        } while (lastDoc && totalFound < 10000);

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const downloadProfilesFromFirestore = async (dispatch, onProgress) => {
    try {
        const { auth, firestore } = await import('../../config/firebase');
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return 0;
        }

        const { userDb } = await import('../../config/pouch');
        let downloaded = 0;
        let totalFound = 0;
        let lastDoc = null;
        const batchSize = 500;

        do {
            let query = firestore.collection('users')
                .orderBy('__name__')
                .limit(batchSize);

            if (lastDoc) {
                query = query.startAfter(lastDoc);
            }

            const snapshot = await query.get();
            const batchCount = snapshot.size;
            totalFound += batchCount;

            if (snapshot.empty) break;

            const batch = [];
            for (const doc of snapshot.docs) {
                const profileData = doc.data();
                batch.push({
                    _id: doc.id,
                    ...profileData,
                    downloadedAt: new Date().toISOString(),
                    source: 'firestore'
                });
                lastDoc = doc;
            }

            try {
                const bulkResult = await userDb.bulkDocs(batch, { new_edits: false });
                bulkResult.forEach(result => {
                    if (!result.error) downloaded++;
                });

                onProgress?.({
                    type: 'profiles',
                    current: downloaded,
                    total: totalFound,
                    progress: Math.round((downloaded / Math.max(totalFound, 500)) * 100),
                    message: `${downloaded}/${totalFound} profils téléchargés`
                });

            } catch (bulkError) {
                for (const profile of batch) {
                    try {
                        await userDb.put(profile);
                        downloaded++;
                    } catch (error) {
                        if (error.status === 409) {
                            try {
                                const existing = await userDb.get(profile._id);
                                await userDb.put({ ...existing, ...profile });
                                downloaded++;
                            } catch (updateError) {}
                        }
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, 50));

        } while (lastDoc && totalFound < 5000);

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const downloadEquivalencesFromFirestore = async (dispatch, onProgress) => {
    try {
        const { auth, firestore } = await import('../../config/firebase');
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return 0;
        }

        const { localDataDb } = await import('../../config/pouch');
        let downloaded = 0;
        let totalFound = 0;
        let lastDoc = null;
        const batchSize = 1000;

        do {
            let query = firestore.collection('equivalences')
                .orderBy('__name__')
                .limit(batchSize);

            if (lastDoc) {
                query = query.startAfter(lastDoc);
            }

            const snapshot = await query.get();
            const batchCount = snapshot.size;
            totalFound += batchCount;

            if (snapshot.empty) break;

            const batch = [];
            for (const doc of snapshot.docs) {
                const data = doc.data();
                batch.push({
                    _id: `equiv_${doc.id}`,
                    ...data,
                    downloadedAt: new Date().toISOString(),
                    source: 'firestore',
                    type: 'equivalence'
                });
                lastDoc = doc;
            }

            try {
                const bulkResult = await localDataDb.bulkDocs(batch, { new_edits: false });
                bulkResult.forEach(result => {
                    if (!result.error) downloaded++;
                });

                // Rapport de progression
                onProgress?.({
                    type: 'equivalences',
                    current: downloaded,
                    total: totalFound,
                    progress: Math.round((downloaded / Math.max(totalFound, 1000)) * 100),
                    message: `${downloaded}/${totalFound} équivalences téléchargées`
                });

            } catch (bulkError) {
                for (const item of batch) {
                    try {
                        await localDataDb.put(item);
                        downloaded++;
                    } catch (error) {
                        if (error.status === 409) {
                            try {
                                const existing = await localDataDb.get(item._id);
                                await localDataDb.put({ ...existing, ...item });
                                downloaded++;
                            } catch (updateError) {}
                        }
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, 50));

        } while (lastDoc && totalFound < 10000);

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const downloadInteractionsFromFirestore = async (dispatch, onProgress) => {
    try {
        const { auth, firestore } = await import('../../config/firebase');
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return 0;
        }

        const { localDataDb } = await import('../../config/pouch');
        let downloaded = 0;
        let totalFound = 0;
        let lastDoc = null;
        const batchSize = 1000;

        do {
            let query = firestore.collection('interactions')
                .orderBy('__name__')
                .limit(batchSize);

            if (lastDoc) {
                query = query.startAfter(lastDoc);
            }

            const snapshot = await query.get();
            const batchCount = snapshot.size;
            totalFound += batchCount;

            if (snapshot.empty) break;

            const batch = [];
            for (const doc of snapshot.docs) {
                const data = doc.data();
                batch.push({
                    _id: `inter_${doc.id}`,
                    ...data,
                    downloadedAt: new Date().toISOString(),
                    source: 'firestore',
                    type: 'interaction'
                });
                lastDoc = doc;
            }

            try {
                const bulkResult = await localDataDb.bulkDocs(batch, { new_edits: false });
                bulkResult.forEach(result => {
                    if (!result.error) downloaded++;
                });

                // Rapport de progression
                onProgress?.({
                    type: 'interactions',
                    current: downloaded,
                    total: totalFound,
                    progress: Math.round((downloaded / Math.max(totalFound, 1000)) * 100),
                    message: `${downloaded}/${totalFound} interactions téléchargées`
                });

            } catch (bulkError) {
                for (const item of batch) {
                    try {
                        await localDataDb.put(item);
                        downloaded++;
                    } catch (error) {
                        if (error.status === 409) {
                            try {
                                const existing = await localDataDb.get(item._id);
                                await localDataDb.put({ ...existing, ...item });
                                downloaded++;
                            } catch (updateError) {}
                        }
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, 50));

        } while (lastDoc && totalFound < 10000);

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const downloadPosologiesFromFirestore = async (dispatch, onProgress) => {
    try {
        const { auth, firestore } = await import('../../config/firebase');
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return 0;
        }

        const { localDataDb } = await import('../../config/pouch');
        let downloaded = 0;
        let totalFound = 0;
        let lastDoc = null;
        const batchSize = 1000;

        do {
            let query = firestore.collection('posologies')
                .orderBy('__name__')
                .limit(batchSize);

            if (lastDoc) {
                query = query.startAfter(lastDoc);
            }

            const snapshot = await query.get();
            const batchCount = snapshot.size;
            totalFound += batchCount;

            if (snapshot.empty) break;

            const batch = [];
            for (const doc of snapshot.docs) {
                const data = doc.data();
                batch.push({
                    _id: `poso_${doc.id}`,
                    ...data,
                    downloadedAt: new Date().toISOString(),
                    source: 'firestore',
                    type: 'posologie'
                });
                lastDoc = doc;
            }

            try {
                const bulkResult = await localDataDb.bulkDocs(batch, { new_edits: false });
                bulkResult.forEach(result => {
                    if (!result.error) downloaded++;
                });

                // Rapport de progression
                onProgress?.({
                    type: 'posologies',
                    current: downloaded,
                    total: totalFound,
                    progress: Math.round((downloaded / Math.max(totalFound, 1000)) * 100),
                    message: `${downloaded}/${totalFound} posologies téléchargées`
                });

            } catch (bulkError) {
                for (const item of batch) {
                    try {
                        await localDataDb.put(item);
                        downloaded++;
                    } catch (error) {
                        if (error.status === 409) {
                            try {
                                const existing = await localDataDb.get(item._id);
                                await localDataDb.put({ ...existing, ...item });
                                downloaded++;
                            } catch (updateError) {}
                        }
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, 50));

        } while (lastDoc && totalFound < 10000);

        return downloaded;

    } catch (error) {
        return 0;
    }
};

const downloadLaboratoiresFromFirestore = async (dispatch, onProgress) => {
    try {
        const { auth, firestore } = await import('../../config/firebase');
        const currentUser = auth.currentUser;

        if (!currentUser) {
            return 0;
        }

        const { localDataDb } = await import('../../config/pouch');
        let downloaded = 0;
        let totalFound = 0;
        let lastDoc = null;
        const batchSize = 1000;

        do {
            let query = firestore.collection('laboratoires')
                .orderBy('__name__')
                .limit(batchSize);

            if (lastDoc) {
                query = query.startAfter(lastDoc);
            }

            const snapshot = await query.get();
            const batchCount = snapshot.size;
            totalFound += batchCount;

            if (snapshot.empty) break;

            const batch = [];
            for (const doc of snapshot.docs) {
                const data = doc.data();
                batch.push({
                    _id: `lab_${doc.id}`,
                    ...data,
                    downloadedAt: new Date().toISOString(),
                    source: 'firestore',
                    type: 'laboratoire'
                });
                lastDoc = doc;
            }

            try {
                const bulkResult = await localDataDb.bulkDocs(batch, { new_edits: false });
                bulkResult.forEach(result => {
                    if (!result.error) downloaded++;
                });

                // Rapport de progression
                onProgress?.({
                    type: 'laboratoires',
                    current: downloaded,
                    total: totalFound,
                    progress: Math.round((downloaded / Math.max(totalFound, 500)) * 100),
                    message: `${downloaded}/${totalFound} laboratoires téléchargés`
                });

            } catch (bulkError) {
                for (const item of batch) {
                    try {
                        await localDataDb.put(item);
                        downloaded++;
                    } catch (error) {
                        if (error.status === 409) {
                            try {
                                const existing = await localDataDb.get(item._id);
                                await localDataDb.put({ ...existing, ...item });
                                downloaded++;
                            } catch (updateError) {}
                        }
                    }
                }
            }

            await new Promise(resolve => setTimeout(resolve, 50));

        } while (lastDoc && totalFound < 5000);

        return downloaded;

    } catch (error) {
        return 0;
    }
};

// Fonctions de statistiques MongoDB copiées du Dashboard
const getMongoDBStats = async () => {
    try {
        const headers = await getAuthHeaders();

        const endpoints = [
            { name: 'products', url: `${API_BASE_URL}/allProducts` },
            { name: 'substances', url: `${API_BASE_URL}/allSubstances` },
            { name: 'profiles', url: `${API_BASE_URL}/allProfiles` },
            { name: 'equivalences', url: `${API_BASE_URL}/allEquivalences` },
            { name: 'interactions', url: `${API_BASE_URL}/allInteractions` },
            { name: 'posologies', url: `${API_BASE_URL}/allPosologies` },
            { name: 'laboratoires', url: `${API_BASE_URL}/allLaboratoires` }
        ];

        const stats = {};

        for (const endpoint of endpoints) {
            try {
                const response = await axios.get(endpoint.url, {
                    headers,
                    timeout: 10000
                });

                const data = response.data;
                const count = data[endpoint.name]?.length || data.length || 0;
                stats[endpoint.name] = count;

            } catch (error) {
                stats[endpoint.name] = 0;
            }
        }

        return stats;
    } catch (error) {
        return {
            products: 0,
            substances: 0,
            profiles: 0,
            equivalences: 0,
            interactions: 0,
            posologies: 0,
            laboratoires: 0
        };
    }
};

// Fonction pour obtenir les statistiques de comptage depuis MongoDB
const getMongoDBProductsCount = async () => {
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/allProducts`, {
            headers,
            timeout: 10000
        });
        return response.data.products?.length || 0;
    } catch (error) {
        return 0;
    }
};

const getMongoDBSubstancesCount = async () => {
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/allSubstances`, {
            headers,
            timeout: 10000
        });
        return response.data.substances?.length || 0;
    } catch (error) {
        return 0;
    }
};

const getMongoDBEquivalencesCount = async () => {
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/allEquivalences`, {
            headers,
            timeout: 10000
        });
        return response.data.equivalences?.length || 0;
    } catch (error) {
        return 0;
    }
};

const getMongoDBInteractionsCount = async () => {
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/allInteractions`, {
            headers,
            timeout: 10000
        });
        return response.data.interactions?.length || 0;
    } catch (error) {
        return 0;
    }
};

const getMongoDBPosologiesCount = async () => {
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/allPosologies`, {
            headers,
            timeout: 10000
        });
        return response.data.posologies?.length || 0;
    } catch (error) {
        return 0;
    }
};

const getMongoDBLaboratoiresCount = async () => {
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get(`${API_BASE_URL}/allLaboratoires`, {
            headers,
            timeout: 10000
        });
        return response.data.laboratoires?.length || 0;
    } catch (error) {
        return 0;
    }
};

export const downloadOfflineData = (onProgress) => async (dispatch) => {
    try {
        dispatch({ type: DOWNLOAD_OFFLINE_DATA_REQUEST });
        dispatch(setLoading(true));
        dispatch(setMessage({
            message: 'Téléchargement des données en cours...',
            status: 'info'
        }));

        const results = {};
        const errors = [];
        const phases = ['products_mongo', 'substances_mongo', 'profiles_mongo', 'products_firestore', 'substances_firestore', 'profiles_firestore', 'equivalences', 'interactions', 'posologies', 'laboratoires', 'json_data'];
        let currentPhaseIndex = 0;

        // Helper function to update progress
        const updateProgress = (phase, message, current = 0, total = 0) => {
            const overallProgress = ((currentPhaseIndex / phases.length) * 100);
            onProgress?.({
                phase,
                message,
                progress: Math.min(overallProgress, 100),
                current,
                total
            });
        };


        // TÉLÉCHARGEMENT DEPUIS MONGODB

        try {
            currentPhaseIndex = 0;
            updateProgress('products_mongo', 'Téléchargement des produits depuis MongoDB...');
            results.productsMongo = await downloadProducts(dispatch, (progress) => {
                updateProgress('products_mongo', `Produits MongoDB: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Produits MongoDB: ${error.message}`);
            results.productsMongo = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('substances_mongo', 'Téléchargement des substances depuis MongoDB...');
            results.substancesMongo = await downloadSubstances(dispatch, (progress) => {
                updateProgress('substances_mongo', `Substances MongoDB: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Substances MongoDB: ${error.message}`);
            results.substancesMongo = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('profiles_mongo', 'Téléchargement des profils depuis MongoDB...');
            results.profilesMongo = await downloadProfiles(dispatch, (progress) => {
                updateProgress('profiles_mongo', `Profils MongoDB: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Profils MongoDB: ${error.message}`);
            results.profilesMongo = 0;
            currentPhaseIndex++;
        }

        // TÉLÉCHARGEMENT DEPUIS FIRESTORE

        try {
            updateProgress('products_firestore', 'Téléchargement des produits depuis Firestore...');
            results.productsFirestore = await downloadProductsFromFirestore(dispatch, (progress) => {
                updateProgress('products_firestore', `Produits Firestore: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Produits Firestore: ${error.message}`);
            results.productsFirestore = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('substances_firestore', 'Téléchargement des substances depuis Firestore...');
            results.substancesFirestore = await downloadSubstancesFromFirestore(dispatch, (progress) => {
                updateProgress('substances_firestore', `Substances Firestore: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Substances Firestore: ${error.message}`);
            results.substancesFirestore = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('profiles_firestore', 'Téléchargement des profils depuis Firestore...');
            results.profilesFirestore = await downloadProfilesFromFirestore(dispatch, (progress) => {
                updateProgress('profiles_firestore', `Profils Firestore: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Profils Firestore: ${error.message}`);
            results.profilesFirestore = 0;
            currentPhaseIndex++;
        }

        // TÉLÉCHARGEMENT DES DONNÉES SUPPLÉMENTAIRES
        try {
            updateProgress('equivalences', 'Téléchargement des équivalences depuis Firestore...');
            results.equivalences = await downloadEquivalencesFromFirestore(dispatch, (progress) => {
                updateProgress('equivalences', `Équivalences: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Équivalences: ${error.message}`);
            results.equivalences = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('interactions', 'Téléchargement des interactions depuis Firestore...');
            results.interactions = await downloadInteractionsFromFirestore(dispatch, (progress) => {
                updateProgress('interactions', `Interactions: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Interactions: ${error.message}`);
            results.interactions = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('posologies', 'Téléchargement des posologies depuis Firestore...');
            results.posologies = await downloadPosologiesFromFirestore(dispatch, (progress) => {
                updateProgress('posologies', `Posologies: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Posologies: ${error.message}`);
            results.posologies = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('laboratoires', 'Téléchargement des laboratoires depuis Firestore...');
            results.laboratoires = await downloadLaboratoiresFromFirestore(dispatch, (progress) => {
                updateProgress('laboratoires', `Laboratoires: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Laboratoires: ${error.message}`);
            results.laboratoires = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('json_data', 'Copie des données JSON locales...');
            results.jsonData = await copyLocalJsonData(dispatch, (progress) => {
                updateProgress('json_data', `Données JSON: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Données JSON: ${error.message}`);
            results.jsonData = 0;
            currentPhaseIndex++;
        }

        // CONSOLIDATION DES RÉSULTATS
        const totalProducts = (results.productsMongo || 0) + (results.productsFirestore || 0);
        const totalSubstances = (results.substancesMongo || 0) + (results.substancesFirestore || 0);
        const totalProfiles = (results.profilesMongo || 0) + (results.profilesFirestore || 0);

        const finalResults = {
            ...results,
            totalProducts,
            totalSubstances,
            totalProfiles,
            totalElements: totalProducts + totalSubstances + totalProfiles +
                          (results.equivalences || 0) + (results.interactions || 0) +
                          (results.posologies || 0) + (results.laboratoires || 0) +
                          (results.jsonData || 0)
        };

        if (errors.length > 0) {
            // Silent error handling
        }

        dispatch({ type: DOWNLOAD_OFFLINE_DATA_SUCCESS, payload: finalResults });
        dispatch(setOfflineDownloadComplete(true));

        const summaryMessage = [
            `Téléchargement terminé ! ${finalResults.totalElements} éléments au total:`,
            `• Produits: ${totalProducts}`,
            `• Substances: ${totalSubstances}`,
            `• Profils: ${totalProfiles}`,
            `• Équivalences: ${results.equivalences || 0}`,
            `• Interactions: ${results.interactions || 0}`,
            `• Posologies: ${results.posologies || 0}`,
            `• Laboratoires: ${results.laboratoires || 0}`
        ].join('\n');

        dispatch(setMessage({
            message: summaryMessage,
            status: 'success'
        }));

        onProgress?.({
            phase: 'complete',
            message: 'Téléchargement de toutes les données terminé !',
            progress: 100,
            results: finalResults
        });

        return finalResults;

    } catch (error) {
        dispatch({ type: DOWNLOAD_OFFLINE_DATA_FAILURE, payload: error.message });
        dispatch(setMessage({
            message: `Erreur lors du téléchargement : ${error.message}`,
            status: 'danger'
        }));

        onProgress?.({
            phase: 'error',
            message: `Erreur : ${error.message}`,
            progress: 0,
            error
        });

        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};

export const downloadOfflineDataFromMongoDB = (onProgress) => async (dispatch) => {
    try {
        dispatch({ type: DOWNLOAD_OFFLINE_DATA_REQUEST });
        dispatch(setLoading(true));
        dispatch(setMessage({
            message: 'Téléchargement des données depuis MongoDB en cours...',
            status: 'info'
        }));

        const results = {};
        const errors = [];
        const phases = ['products', 'substances', 'profiles', 'json_data'];
        let currentPhaseIndex = 0;

        const updateProgress = (phase, message, current = 0, total = 0) => {
            const overallProgress = ((currentPhaseIndex / phases.length) * 100);
            onProgress?.({
                phase,
                message,
                progress: Math.min(overallProgress, 100),
                current,
                total
            });
        };

        try {
            currentPhaseIndex = 0;
            updateProgress('products', 'Téléchargement des produits depuis MongoDB...');
            results.products = await downloadProducts(dispatch, (progress) => {
                updateProgress('products', `Produits: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Produits: ${error.message}`);
            results.products = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('substances', 'Téléchargement des substances depuis MongoDB...');
            results.substances = await downloadSubstances(dispatch, (progress) => {
                updateProgress('substances', `Substances: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Substances: ${error.message}`);
            results.substances = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('profiles', 'Téléchargement des profils depuis MongoDB...');
            results.profiles = await downloadProfiles(dispatch, (progress) => {
                updateProgress('profiles', `Profils: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Profils: ${error.message}`);
            results.profiles = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('json_data', 'Copie des données JSON locales...');
            results.jsonData = await copyLocalJsonData(dispatch, (progress) => {
                updateProgress('json_data', `Données JSON: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Données JSON: ${error.message}`);
            results.jsonData = 0;
            currentPhaseIndex++;
        }

        dispatch({ type: DOWNLOAD_OFFLINE_DATA_SUCCESS, payload: results });
        dispatch(setOfflineDownloadComplete(true));
        dispatch(setMessage({
            message: `Téléchargement MongoDB terminé ! ${(results.products || 0) + (results.substances || 0) + (results.profiles || 0)} éléments téléchargés.`,
            status: 'success'
        }));

        onProgress?.({
            phase: 'complete',
            message: 'Téléchargement MongoDB terminé !',
            progress: 100,
            results
        });

        return results;

    } catch (error) {
        dispatch({ type: DOWNLOAD_OFFLINE_DATA_FAILURE, payload: error.message });
        dispatch(setMessage({
            message: `Erreur lors du téléchargement MongoDB : ${error.message}`,
            status: 'danger'
        }));

        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};

export const downloadOfflineDataFromFirestore = (onProgress) => async (dispatch) => {
    try {
        dispatch({ type: DOWNLOAD_OFFLINE_DATA_REQUEST });
        dispatch(setLoading(true));
        dispatch(setMessage({
            message: 'Téléchargement des données depuis Firestore en cours...',
            status: 'info'
        }));

        const results = {};
        const errors = [];
        const phases = ['products', 'substances', 'profiles', 'equivalences', 'interactions', 'posologies', 'laboratoires', 'json_data'];
        let currentPhaseIndex = 0;

        const updateProgress = (phase, message, current = 0, total = 0) => {
            const overallProgress = ((currentPhaseIndex / phases.length) * 100);
            onProgress?.({
                phase,
                message,
                progress: Math.min(overallProgress, 100),
                current,
                total
            });
        };

        try {
            currentPhaseIndex = 0;
            updateProgress('products', 'Téléchargement des produits depuis Firestore...');
            results.products = await downloadProductsFromFirestore(dispatch, (progress) => {
                updateProgress('products', `Produits: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Produits: ${error.message}`);
            results.products = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('substances', 'Téléchargement des substances depuis Firestore...');
            results.substances = await downloadSubstancesFromFirestore(dispatch, (progress) => {
                updateProgress('substances', `Substances: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Substances: ${error.message}`);
            results.substances = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('profiles', 'Téléchargement des profils depuis Firestore...');
            results.profiles = await downloadProfilesFromFirestore(dispatch, (progress) => {
                updateProgress('profiles', `Profils: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Profils: ${error.message}`);
            results.profiles = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('equivalences', 'Téléchargement des équivalences depuis Firestore...');
            results.equivalences = await downloadEquivalencesFromFirestore(dispatch, (progress) => {
                updateProgress('equivalences', `Équivalences: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Équivalences: ${error.message}`);
            results.equivalences = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('interactions', 'Téléchargement des interactions depuis Firestore...');
            results.interactions = await downloadInteractionsFromFirestore(dispatch, (progress) => {
                updateProgress('interactions', `Interactions: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Interactions: ${error.message}`);
            results.interactions = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('posologies', 'Téléchargement des posologies depuis Firestore...');
            results.posologies = await downloadPosologiesFromFirestore(dispatch, (progress) => {
                updateProgress('posologies', `Posologies: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Posologies: ${error.message}`);
            results.posologies = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('laboratoires', 'Téléchargement des laboratoires depuis Firestore...');
            results.laboratoires = await downloadLaboratoiresFromFirestore(dispatch, (progress) => {
                updateProgress('laboratoires', `Laboratoires: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Laboratoires: ${error.message}`);
            results.laboratoires = 0;
            currentPhaseIndex++;
        }

        try {
            updateProgress('json_data', 'Copie des données JSON locales...');
            results.jsonData = await copyLocalJsonData(dispatch, (progress) => {
                updateProgress('json_data', `Données JSON: ${progress.current || 0}/${progress.total || 0}`, progress.current, progress.total);
            });
            currentPhaseIndex++;
        } catch (error) {
            errors.push(`Données JSON: ${error.message}`);
            results.jsonData = 0;
            currentPhaseIndex++;
        }

        const totalElements = (results.products || 0) + (results.substances || 0) + (results.profiles || 0) +
                            (results.equivalences || 0) + (results.interactions || 0) + (results.posologies || 0) +
                            (results.laboratoires || 0) + (results.jsonData || 0);

        dispatch({ type: DOWNLOAD_OFFLINE_DATA_SUCCESS, payload: results });
        dispatch(setOfflineDownloadComplete(true));
        dispatch(setMessage({
            message: `Téléchargement Firestore terminé ! ${totalElements} éléments téléchargés.`,
            status: 'success'
        }));

        onProgress?.({
            phase: 'complete',
            message: 'Téléchargement Firestore terminé !',
            progress: 100,
            results
        });

        return results;

    } catch (error) {
        dispatch({ type: DOWNLOAD_OFFLINE_DATA_FAILURE, payload: error.message });
        dispatch(setMessage({
            message: `Erreur lors du téléchargement Firestore : ${error.message}`,
            status: 'danger'
        }));

        throw error;
    } finally {
        dispatch(setLoading(false));
    }
};

export const estimateDownloadSize = () => async dispatch => {
    try {
        const requests = [
            axios.get(`${API_BASE_URL}/products/count`, { timeout: 5000 }),
            axios.get(`${API_BASE_URL}/substances/count`, { timeout: 5000 }),
            axios.get(`${API_BASE_URL}/profiles/count`, { timeout: 5000 })
        ];

        const [prodCount, subCount, profCount] = await Promise.all(requests);

        const estimate = {
            products: prodCount.data.count || 0,
            substances: subCount.data.count || 0,
            profiles: profCount.data.count || 0,
            totalItems: (prodCount.data.count || 0) + (subCount.data.count || 0) + (profCount.data.count || 0),
            estimatedSizeMB: Math.round(((prodCount.data.count || 0) * 2 + (subCount.data.count || 0) * 1 + (profCount.data.count || 0) * 0.5) / 100)
        };

        return estimate;
    } catch (error) {
        throw error;
    }
};

export default {
    downloadOfflineData,
    downloadOfflineDataFromMongoDB,
    downloadOfflineDataFromFirestore,
    estimateDownloadSize,
    getMongoDBStats,
    getMongoDBProductsCount,
    getMongoDBSubstancesCount,
    getMongoDBEquivalencesCount,
    getMongoDBInteractionsCount,
    getMongoDBPosologiesCount,
    getMongoDBLaboratoiresCount
};

